import React, { useEffect, useState } from 'react'
import SearchForm from '../components/SearchForm'
import SearchResults from '../components/SearchResults'
import ConfiguratorPage from './ConfiguratorPage'
import { useSearchStore } from '../store/searchStore'
import { useAuthStore } from '../store/authStore'
import { FaWhatsapp } from 'react-icons/fa'
import { FiMail, FiSliders, FiSearch } from 'react-icons/fi'
import { buildAuthWhatsAppURL } from '../config/whatsapp'
import ContactModal from '../components/ContactModal'

function SearchPage() {
  const { results, loading, searchedDimensions, processes, selectedProcess, setSelectedProcess, search, setSelectedSeries } = useSearchStore()
  const { user, isAdmin } = useAuthStore()
  const [showContactModal, setShowContactModal] = useState(false)
  const [viewMode, setViewMode] = useState('classic') // 'classic' | 'configurator'

  // Persist configurator state across view toggles
  const [configState, setConfigState] = useState({
    lengthLow: null, lengthHigh: null,
    widthLow: null, widthHigh: null,
    depthLow: null, depthHigh: null,
  })

  const mfrs = user?.allowedManufacturers || []
  const hasAccess = isAdmin || mfrs.includes('*') || mfrs.length > 0

  const switchToOtherProcess = () => {
    // Find the other process (if current is Injection Mold, switch to Rotomold)
    const otherProcess = processes.find(p => p.name !== selectedProcess)
    if (otherProcess && searchedDimensions) {
      setSelectedProcess(otherProcess.name)
      // Get all series for the new process's manufacturers
      const allSeries = otherProcess.manufacturers.flatMap(m => m.series)
      setSelectedSeries(allSeries)
      // Search with same dimensions but new process and all series
      setTimeout(() => {
        search(searchedDimensions.length, searchedDimensions.width, searchedDimensions.depth)
      }, 0)
    }
  }

  if (!hasAccess) {
    return (
      <>
        {showContactModal && <ContactModal onClose={() => setShowContactModal(false)} />}
        <main className="max-w-xl mx-auto px-4 py-16 text-center">
          <div className="card space-y-4">
            <div className="text-5xl">🔒</div>
            <h2 className="text-xl font-bold text-white">No Product Ranges Assigned Yet</h2>
            <p className="text-gray-400 text-sm">You don't have access to any product ranges yet. Please contact us to request access.</p>
            <div className="flex gap-3 justify-center pt-2">
              <a href={buildAuthWhatsAppURL()} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 rounded bg-[#25D366]/10 border border-[#25D366]/30 text-[#25D366] text-sm hover:bg-[#25D366]/20 transition">
                <FaWhatsapp size={16} /> WhatsApp Us
              </a>
              <button
                onClick={() => setShowContactModal(true)}
                className="flex items-center gap-2 px-4 py-2 rounded bg-gray-700/30 border border-border-dark text-gray-300 text-sm hover:bg-gray-700/50 transition">
                <FiMail size={15} /> Email Us
              </button>
            </div>
          </div>
        </main>
      </>
    )
  }

  const noResultsAfterSearch = !loading && searchedDimensions !== null && results.length === 0

  return (
    <>
    {showContactModal && <ContactModal onClose={() => setShowContactModal(false)} />}
    <main className="max-w-7xl 2xl:max-w-screen-2xl 3xl:max-w-screen-3xl 4xl:max-w-screen-4xl mx-auto px-4 sm:px-6 lg:px-8 2xl:px-16 3xl:px-24 py-8 2xl:py-12 3xl:py-16">

      {/* View toggle */}
      <div className="flex items-center gap-2 mb-6">
        <button
          onClick={() => setViewMode('classic')}
          className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full border text-sm font-semibold transition ${viewMode === 'classic' ? 'border-accent-yellow bg-accent-yellow/10 text-accent-yellow' : 'border-border-dark text-gray-500 hover:text-gray-300 hover:border-gray-500'}`}
        >
          <FiSearch size={13} /> Classic
        </button>
        <button
          onClick={() => setViewMode('configurator')}
          className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full border text-sm font-semibold transition ${viewMode === 'configurator' ? 'border-accent-teal bg-accent-teal/10 text-accent-teal' : 'border-border-dark text-gray-500 hover:text-gray-300 hover:border-gray-500'}`}
        >
          <FiSliders size={13} /> Configurator
        </button>
      </div>

      {/* Configurator view */}
      <div className={viewMode === 'configurator' ? '' : 'hidden'}>
        <ConfiguratorPage
          onSwitchToClassic={() => setViewMode('classic')}
          savedState={configState}
          onStateChange={setConfigState}
        />
      </div>

      {/* Classic view — kept mounted to preserve SearchForm local state (dims survive tab switch) */}
      <div className={viewMode === 'classic' ? '' : 'hidden'}>
      {results.length > 0 && (
        <p className="mb-3 text-xs 3xl:text-sm text-gray-500 text-center border-b border-border-dark pb-2 -mt-6">
          The Case Sizing Engine is an online tool designed to offer case size suggestions that best fit the users application. The final decision is the sole responsibility of the user.
        </p>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-3 2xl:grid-cols-4 gap-8 2xl:gap-12 3xl:gap-16">
        {/* Search Form */}
        <div className="lg:col-span-1 flex flex-col gap-8">
          <SearchForm />
          {results.length > 0 && (
            <img
              src="/case-image-800.jpg"
              alt="Case sizing reference"
              className="hidden lg:block w-full rounded-lg object-cover"
            />
          )}
        </div>

        {/* Results */}
        <div className="lg:col-span-2 2xl:col-span-3">
          {results.length > 0 && (
            <SearchResults />
          )}
          {noResultsAfterSearch && (
            <div className="space-y-4">
              {selectedProcess === 'Injection Mold' && processes.length > 1 ? (
                // Prominent warning — same style as the form's volume warning
                <div className="p-4 bg-orange-900/30 border border-orange-600/50 rounded space-y-3">
                  <p className="flex items-start gap-2 text-orange-200 text-sm">
                    <span className="text-xl">⚠️</span>
                    <span>
                      No Injection Mold cases found for{' '}
                      <span className="font-semibold text-orange-100">
                        {searchedDimensions.length} × {searchedDimensions.width} × {searchedDimensions.depth} cm
                      </span>.
                      <br />Consider searching our larger Rotomold cases for better options.
                    </span>
                  </p>
                  <button
                    onClick={switchToOtherProcess}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded bg-orange-600 text-white font-semibold text-sm hover:bg-orange-700 transition"
                  >
                    Search Rotomold Cases
                  </button>
                </div>
              ) : (
                // Rotomold or single-process: show contact
                <div className="card text-center py-10 space-y-4">
                  <div className="text-5xl">🔍</div>
                  <h3 className="text-xl font-bold text-white">No Matching Cases Found</h3>
                  <p className="text-gray-400 text-sm max-w-md mx-auto">
                    We don't currently have a product solution for{' '}
                    <span className="text-accent-yellow font-semibold">
                      {searchedDimensions.length} × {searchedDimensions.width} × {searchedDimensions.depth} cm
                    </span>.
                    Please contact us and we'll try to assist you.
                  </p>
                  <button
                    onClick={() => setShowContactModal(true)}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded bg-accent-yellow text-bg-dark font-semibold text-sm hover:bg-[#c96f02] transition"
                  >
                    <FiMail size={16} /> Contact Us
                  </button>
                </div>
              )}
            </div>
          )}
          {!noResultsAfterSearch && results.length === 0 && (
            <div className="card text-center py-6 text-gray-400 space-y-4">
              <p className="text-lg">Enter dimensions and click "Find Cases" to search</p>
              <img
                src="/case-image-800.jpg"
                alt="Case sizing reference"
                className="w-96 mx-auto rounded-lg object-cover"
              />
            </div>
          )}
        </div>
      </div>

      {results.length > 0 && (
        <>
          <img
            src="/case-image-800.jpg"
            alt="Case sizing reference"
            className="block md:hidden w-full rounded-lg object-cover mt-6"
          />
          <img
            src="/case-image-800.jpg"
            alt="Case sizing reference"
            className="hidden md:block lg:hidden w-full rounded-lg object-cover mt-6"
          />
        </>
      )}

      {results.length === 0 && (
        <p className="mt-6 text-xs text-gray-500 text-center border-t border-border-dark pt-4">
          The Case Sizing Engine is an online tool designed to offer case size suggestions that best fit the users application. The final decision is the sole responsibility of the user.
        </p>
      )}
      </div>
    </main>
    </>
  )
}

export default SearchPage

